import {
  ALLOW_UPDATE_USER_ADDRESS_CONDITION,
  AMOUNT_CONDITION,
  AMOUNT_PERCENT_CONDITION,
  IMMUTABLE_CONDITION,
  NO_ACTIVE_CONDITION,
  SYMBOL_CONDITION,
  VOTING_CONDITION,
  VOTING_MANDATORY_CONDITION,
} from "../../utils/constants";
import { ISafeObject, SafeObjectUtils } from "./ISafeObject";
// import { Policy } from "./cmds/Policy"; // TODO: Handle Policy interface conversion

// import { Param } from "./Param"; // TODO: Handle Param interface conversion

// Base Condition interface
export interface Condition extends ISafeObject {
  readonly name: string;
}

// Specific condition interfaces
export interface AmountCondition extends Condition {
  readonly min: number;
  readonly max: number;
}

export interface SymbolCondition extends Condition {
  readonly symbols: string[];
}

export interface AmountPercentCondition extends Condition {
  readonly min: number;
  readonly max: number;
}

export interface InsideGroupVoteCondition extends Condition {
  readonly qty: number;
  readonly isPercentage: boolean;
}

export interface NoActiveCondition extends Condition {
  readonly params: any[]; // TODO: Replace with IParam[] when Param is converted
}

export interface RecoveryCondition extends Condition {
  readonly params: any[]; // TODO: Replace with IParam[] when Param is converted
}

export interface UpdateUserAddressCondition extends Condition {
  readonly params: any[]; // TODO: Replace with IParam[] when Param is converted
}

// Utility functions for Condition
export const ConditionUtils = {
  // Create a new ICondition
  create({ cls, name }: { cls: string; name: string }): Condition {
    return { cls, name };
  },

  // Create empty condition
  empty(): Condition {
    return { cls: "", name: "" };
  },

  // Factory method to create specific condition from map
  fromMap(map: Record<string, any>): Condition {
    switch (map["class"]) {
      case SYMBOL_CONDITION:
        return SymbolConditionUtils.fromMap(map);
      case AMOUNT_CONDITION:
        return AmountConditionUtils.fromMap(map);
      case VOTING_CONDITION:
        return InsideGroupVoteConditionUtils.fromMap(map);
      case IMMUTABLE_CONDITION:
        return ConditionUtils.create({ cls: map["class"], name: "IMMUTABLE" });
      case NO_ACTIVE_CONDITION:
        return NoActiveConditionUtils.fromMap(map);
      case VOTING_MANDATORY_CONDITION:
        return RecoveryConditionUtils.fromMap(map);
      case AMOUNT_PERCENT_CONDITION:
        return AmountPercentConditionUtils.fromMap(map);
      case ALLOW_UPDATE_USER_ADDRESS_CONDITION:
        return UpdateUserAddressConditionUtils.fromMap(map);
      default:
        return ConditionUtils.create({ cls: map["class"], name: "Unknown" });
    }
  },

  // Helper method to get parameter from params array
  getParam({ params, key }: { params: any[]; key: string }): Record<string, any> {
    const emptyObject = { key, value: "" };
    return params.find((p) => p["key"] === key) || emptyObject;
  },

  // Helper method to get integer parameter
  getInt({ params, key }: { params: any[]; key: string }): number {
    const intParam = String(ConditionUtils.getParam({ params, key })["value"]);
    const parsed = parseInt(intParam);
    return isNaN(parsed) ? 0 : parsed;
  },

  // Helper method to get double parameter
  getDouble({ params, key }: { params: any[]; key: string }): number {
    const intParam = String(ConditionUtils.getParam({ params, key })["value"]);
    const parsed = parseFloat(intParam);
    return isNaN(parsed) ? 0 : parsed;
  },

  // Helper method to get boolean parameter
  getBool({
    params,
    key,
    defaultVal,
  }: {
    params: any[];
    key: string;
    defaultVal?: boolean;
  }): boolean {
    const intParam = String(ConditionUtils.getParam({ params, key })["value"]);
    return intParam === "true" || !!defaultVal;
  },

  // Convert to map
  toMap(condition: Condition): Record<string, any> {
    const json = SafeObjectUtils.toMap(condition);
    json["name"] = condition.name;
    json["class"] = condition.cls;
    switch (condition.cls) {
      case SYMBOL_CONDITION:
        return { ...json, ...SymbolConditionUtils.toMap(condition as SymbolCondition) };
      case AMOUNT_CONDITION:
        return { ...json, ...AmountConditionUtils.toMap(condition as AmountCondition) };
      case VOTING_CONDITION:
        return {
          ...json,
          ...InsideGroupVoteConditionUtils.toMap(condition as InsideGroupVoteCondition),
        };
      case NO_ACTIVE_CONDITION:
        return { ...json, ...NoActiveConditionUtils.toMap(condition as NoActiveCondition) };
      case VOTING_MANDATORY_CONDITION:
        return { ...json, ...RecoveryConditionUtils.toMap(condition as RecoveryCondition) };
      case AMOUNT_PERCENT_CONDITION:
        return {
          ...json,
          ...AmountPercentConditionUtils.toMap(condition as AmountPercentCondition),
        };
      case ALLOW_UPDATE_USER_ADDRESS_CONDITION:
        return {
          ...json,
          ...UpdateUserAddressConditionUtils.toMap(condition as UpdateUserAddressCondition),
        };
      default:
        return json;
    }
  },

  // Convert to JSON
  toJson(condition: Condition): string {
    return JSON.stringify(ConditionUtils.toMap(condition));
  },

  // Validate condition
  validate(condition: Condition): boolean {
    return true;
  },

  // TODO: Uncomment when Policy interface is ready
  // fromPolicy(condition: ICondition, policy: Policy): ICondition {
  //   return policy.conditions.find((c) => c.cls === condition.cls)!;
  // },

  // indexFromPolicy(condition: ICondition, policy: Policy): number {
  //   return policy.conditions.findIndex((c) => c.cls === condition.cls);
  // },
};

// AmountCondition utilities
export const AmountConditionUtils = {
  // Create new IAmountCondition
  create({ min, max }: { min: number; max: number }): AmountCondition {
    return {
      cls: AMOUNT_CONDITION,
      name: "Amount condition",
      min,
      max,
    };
  },

  // Create empty AmountCondition
  empty(): AmountCondition {
    return AmountConditionUtils.create({ min: 0, max: 1 });
  },

  // Copy with new values
  copyWith(
    condition: AmountCondition,
    { min, max }: { min?: number; max?: number }
  ): AmountCondition {
    return AmountConditionUtils.create({
      min: min ?? condition.min,
      max: max ?? condition.max,
    });
  },

  // Convert to map
  toMap(condition: AmountCondition): Record<string, any> {
    return {
      // ...ConditionUtils.toMap(condition),
      params: [
        { key: "min", value: condition.min },
        { key: "max", value: condition.max },
      ],
    };
  },

  // Convert to JSON
  toJson(condition: AmountCondition): string {
    return JSON.stringify(AmountConditionUtils.toMap(condition));
  },

  // Create from map
  fromMap(map: Record<string, any>): AmountCondition {
    try {
      if (!Array.isArray(map.params)) {
        throw new Error("Missing params on amount condition");
      }
      const params = map.params;
      const min = ConditionUtils.getInt({ params, key: "min" });
      const max = ConditionUtils.getInt({ params, key: "max" });
      return AmountConditionUtils.create({ min, max });
    } catch (e) {
      console.error(e);
      return AmountConditionUtils.empty();
    }
  },

  // Create from JSON
  fromJson(source: string): AmountCondition {
    return AmountConditionUtils.fromMap(JSON.parse(source));
  },

  // Validate
  validate(condition: AmountCondition): boolean {
    return condition.min > -1 && condition.max > 0 && condition.min < condition.max;
  },

  // TODO: Uncomment when Policy interface is ready
  // fromPolicy(policy: Policy): IAmountCondition {
  //   return (
  //     policy.conditions.find((c) => c.cls === AMOUNT_CONDITION) ??
  //     AmountConditionUtils.empty()
  //   ) as IAmountCondition;
  // },

  // indexFromPolicy(policy: Policy): number {
  //   return policy.conditions.findIndex((c) => c.cls === AMOUNT_CONDITION);
  // },
};

// SymbolCondition utilities
export const SymbolConditionUtils = {
  // Create new ISymbolCondition
  create({ symbols }: { symbols: string[] }): SymbolCondition {
    return {
      cls: SYMBOL_CONDITION,
      name: "Symbols condition",
      symbols,
    };
  },

  // Create empty SymbolCondition
  empty(): SymbolCondition {
    return SymbolConditionUtils.create({ symbols: [] });
  },

  // Copy with new values
  copyWith(condition: SymbolCondition, { symbols }: { symbols?: string[] }): SymbolCondition {
    return SymbolConditionUtils.create({
      symbols: symbols ?? condition.symbols,
    });
  },

  // Convert to map
  toMap(condition: SymbolCondition): Record<string, any> {
    return {
      params: [{ key: "symbols", value: condition.symbols }],
    };
  },

  // Convert to JSON
  toJson(condition: SymbolCondition): string {
    return JSON.stringify(ConditionUtils.toMap(condition));
  },

  // Create from map
  fromMap(map: Record<string, any>): SymbolCondition {
    try {
      if (!Array.isArray(map.params)) {
        throw new Error("Missing params on symbol condition");
      }
      const params = map.params;
      const symbols = (params.find((p) => p.key === "symbols")?.value as string[]) ?? [];
      return SymbolConditionUtils.create({ symbols });
    } catch (e) {
      console.error(e);
      return SymbolConditionUtils.empty();
    }
  },

  // Create from JSON
  fromJson(source: string): SymbolCondition {
    return SymbolConditionUtils.fromMap(JSON.parse(source));
  },

  // Validate
  validate(condition: SymbolCondition): boolean {
    return condition.symbols.length > 0;
  },

  // TODO: Uncomment when Policy interface is ready
  // fromPolicy(policy: Policy): ISymbolCondition {
  //   return (
  //     policy.conditions.find((c) => c.cls === SYMBOL_CONDITION) ??
  //     SymbolConditionUtils.empty()
  //   ) as ISymbolCondition;
  // },

  // indexFromPolicy(policy: Policy): number {
  //   return policy.conditions.findIndex((c) => c.cls === SYMBOL_CONDITION);
  // },
};

// AmountPercentCondition utilities
export const AmountPercentConditionUtils = {
  // Create new IAmountPercentCondition
  create({ min, max }: { min: number; max: number }): AmountPercentCondition {
    return {
      cls: AMOUNT_PERCENT_CONDITION,
      name: "Amount percent condition",
      min,
      max,
    };
  },

  // Create empty AmountPercentCondition
  empty(): AmountPercentCondition {
    return AmountPercentConditionUtils.create({ min: 0, max: 1 });
  },

  // Copy with new values
  copyWith(
    condition: AmountPercentCondition,
    { min, max }: { min?: number; max?: number }
  ): AmountPercentCondition {
    return AmountPercentConditionUtils.create({
      min: min ?? condition.min,
      max: max ?? condition.max,
    });
  },

  // Convert to map
  toMap(condition: AmountPercentCondition): Record<string, any> {
    return {
      params: [
        { key: "min", value: condition.min },
        { key: "max", value: condition.max },
      ],
    };
  },

  // Convert to JSON
  toJson(condition: AmountPercentCondition): string {
    return JSON.stringify(ConditionUtils.toMap(condition));
  },

  // Create from map
  fromMap(map: Record<string, any>): AmountPercentCondition {
    try {
      if (!Array.isArray(map["params"])) {
        throw new Error("Missing params on amount percent condition");
      }
      const params = map["params"];
      const min = ConditionUtils.getDouble({ key: "min", params });
      const max = ConditionUtils.getDouble({ key: "max", params });
      return AmountPercentConditionUtils.create({ min, max });
    } catch (error) {
      console.error(error);
      return AmountPercentConditionUtils.empty();
    }
  },

  // Create from JSON
  fromJson(source: string): AmountPercentCondition {
    return AmountPercentConditionUtils.fromMap(JSON.parse(source));
  },

  // Convert to string
  toString(condition: AmountPercentCondition): string {
    return `AmountPercentCondition(min: ${condition.min}, max: ${condition.max})`;
  },

  // Validate
  validate(condition: AmountPercentCondition): boolean {
    return condition.min > -1 && condition.max > 0 && condition.min < condition.max;
  },

  // TODO: Uncomment when Policy interface is ready
  // fromPolicy(policy: Policy): IAmountPercentCondition {
  //   return (
  //     policy.conditions.find((c) => c.cls === AMOUNT_PERCENT_CONDITION) ||
  //     AmountPercentConditionUtils.empty()
  //   ) as IAmountPercentCondition;
  // },

  // indexFromPolicy(policy: Policy): number {
  //   return policy.conditions.findIndex((c) => c.cls === AMOUNT_PERCENT_CONDITION);
  // },
};

// InsideGroupVoteCondition utilities
export const InsideGroupVoteConditionUtils = {
  // Create new IInsideGroupVoteCondition
  create({ qty, isPercentage }: { qty: number; isPercentage: boolean }): InsideGroupVoteCondition {
    return {
      cls: VOTING_CONDITION,
      name: "Vote condition",
      qty,
      isPercentage,
    };
  },

  // Create empty InsideGroupVoteCondition
  empty(): InsideGroupVoteCondition {
    return InsideGroupVoteConditionUtils.create({ qty: 0, isPercentage: true });
  },

  // Copy with new values
  copyWith(
    condition: InsideGroupVoteCondition,
    { qty, isPercentage }: { qty?: number; isPercentage?: boolean }
  ): InsideGroupVoteCondition {
    return InsideGroupVoteConditionUtils.create({
      qty: qty ?? condition.qty,
      isPercentage: isPercentage ?? condition.isPercentage,
    });
  },

  // Convert to map
  toMap(condition: InsideGroupVoteCondition): Record<string, any> {
    return {
      params: [
        { key: "qty", value: condition.qty },
        { key: "isPercentage", value: condition.isPercentage },
      ],
    };
  },

  // Create from map
  fromMap(map: Record<string, any>): InsideGroupVoteCondition {
    if (!(map.params instanceof Array)) {
      throw new Error("missing params on vote condition");
    }

    const params = map.params;
    const qty = ConditionUtils.getInt({ key: "qty", params });
    const isPercentage = ConditionUtils.getBool({ key: "isPercentage", params, defaultVal: true });

    return InsideGroupVoteConditionUtils.create({ qty, isPercentage });
  },

  // Convert to JSON
  toJson(condition: InsideGroupVoteCondition): string {
    return JSON.stringify(ConditionUtils.toMap(condition));
  },

  // Create from JSON
  fromJson(source: string): InsideGroupVoteCondition {
    return InsideGroupVoteConditionUtils.fromMap(JSON.parse(source));
  },

  // Validate
  validate(condition: InsideGroupVoteCondition): boolean {
    return condition.qty >= 0 && condition.qty <= 100;
  },

  // TODO: Uncomment when Policy interface is ready
  // fromPolicy(policy: Policy): IInsideGroupVoteCondition {
  //   return (policy.conditions.find((c) => c.cls === VOTING_CONDITION) as IInsideGroupVoteCondition) || InsideGroupVoteConditionUtils.empty();
  // },

  // indexFromPolicy(policy: Policy): number {
  //   return policy.conditions.findIndex((c) => c.cls === VOTING_CONDITION);
  // },
};

// NoActiveCondition utilities
export const NoActiveConditionUtils = {
  // Create new INoActiveCondition
  create({ params }: { params: any[] }): NoActiveCondition {
    return {
      cls: NO_ACTIVE_CONDITION,
      name: "No active condition",
      params,
    };
  },

  // Create empty NoActiveCondition
  empty(): NoActiveCondition {
    return NoActiveConditionUtils.create({
      params: [
        /* Param.empty() */
      ],
    }); // TODO: Replace when Param is converted
  },

  // Copy with new values
  copyWith(condition: NoActiveCondition, { params }: { params?: any[] }): NoActiveCondition {
    return NoActiveConditionUtils.create({
      params: params ?? condition.params,
    });
  },

  // Convert to map
  toMap(condition: NoActiveCondition): Record<string, any> {
    return {
      params: condition.params.map((param) => param /* param.toMap() */), // TODO: Replace when Param is converted
    };
  },

  // Convert to JSON
  toJson(condition: NoActiveCondition): string {
    return JSON.stringify(ConditionUtils.toMap(condition));
  },

  // Create from map
  fromMap(map: Record<string, any>): NoActiveCondition {
    try {
      return NoActiveConditionUtils.create({
        params:
          map["params"] && map["params"] !== ""
            ? map["params"].map((e: any) => e /* Param.fromMap(e) */) // TODO: Replace when Param is converted
            : [],
      });
    } catch (e) {
      console.error(e);
      return NoActiveConditionUtils.empty();
    }
  },

  // Create from JSON
  fromJson(source: string): NoActiveCondition {
    return NoActiveConditionUtils.fromMap(JSON.parse(source));
  },

  // Convert to string
  toString(condition: NoActiveCondition): string {
    return `NoActiveCondition(${JSON.stringify(condition.params)})`;
  },

  // Validate
  validate(condition: NoActiveCondition): boolean {
    return condition.params.length > 0;
  },

  // TODO: Uncomment when Policy interface is ready
  // fromPolicy(policy: Policy): INoActiveCondition {
  //   return (
  //     policy.conditions.find((c) => c.cls === NO_ACTIVE_CONDITION) ||
  //     NoActiveConditionUtils.empty()
  //   ) as INoActiveCondition;
  // },

  // indexFromPolicy(policy: Policy): number {
  //   return policy.conditions.findIndex((c) => c.cls === NO_ACTIVE_CONDITION);
  // },
};

// RecoveryCondition utilities
export const RecoveryConditionUtils = {
  // Create new IRecoveryCondition
  create({ params }: { params: any[] }): RecoveryCondition {
    return {
      cls: VOTING_MANDATORY_CONDITION,
      name: "Recovery condition",
      params,
    };
  },

  // Create empty RecoveryCondition
  empty(): RecoveryCondition {
    return RecoveryConditionUtils.create({
      params: [
        /* Param.empty() */
      ],
    }); // TODO: Replace when Param is converted
  },

  // Copy with new values
  copyWith(condition: RecoveryCondition, { params }: { params?: any[] }): RecoveryCondition {
    return RecoveryConditionUtils.create({
      params: params ?? condition.params,
    });
  },

  // Convert to map
  toMap(condition: RecoveryCondition): Record<string, any> {
    return {
      params: condition.params.map((param) => param /* param.toMap() */), // TODO: Replace when Param is converted
    };
  },

  // Convert to JSON
  toJson(condition: RecoveryCondition): string {
    return JSON.stringify(ConditionUtils.toMap(condition));
  },

  // Create from map
  fromMap(map: Record<string, any>): RecoveryCondition {
    try {
      return RecoveryConditionUtils.create({
        params:
          map["params"] && map["params"] !== ""
            ? map["params"].map((e: any) => e /* Param.fromMap(e) */) // TODO: Replace when Param is converted
            : [],
      });
    } catch (e) {
      console.error(e);
      return RecoveryConditionUtils.empty();
    }
  },

  // Create from JSON
  fromJson(source: string): RecoveryCondition {
    return RecoveryConditionUtils.fromMap(JSON.parse(source));
  },

  // Validate
  validate(condition: RecoveryCondition): boolean {
    return condition.params.length > 0;
  },

  // TODO: Uncomment when Policy interface is ready
  // fromPolicy(policy: Policy): IRecoveryCondition {
  //   return (
  //     policy.conditions.find((c) => c.cls === VOTING_MANDATORY_CONDITION) ||
  //     RecoveryConditionUtils.empty()
  //   ) as IRecoveryCondition;
  // },

  // indexFromPolicy(policy: Policy): number {
  //   return policy.conditions.findIndex((c) => c.cls === VOTING_MANDATORY_CONDITION);
  // },
};

// UpdateUserAddressCondition utilities
export const UpdateUserAddressConditionUtils = {
  // Create new IUpdateUserAddressCondition
  create({ params }: { params: any[] }): UpdateUserAddressCondition {
    return {
      cls: ALLOW_UPDATE_USER_ADDRESS_CONDITION,
      name: "Update User Address condition",
      params,
    };
  },

  // Create empty UpdateUserAddressCondition
  empty(): UpdateUserAddressCondition {
    return UpdateUserAddressConditionUtils.create({
      params: [
        /* Param.empty() */
      ],
    }); // TODO: Replace when Param is converted
  },

  // Copy with new values
  copyWith(
    condition: UpdateUserAddressCondition,
    { params }: { params?: any[] }
  ): UpdateUserAddressCondition {
    return UpdateUserAddressConditionUtils.create({
      params: params ?? condition.params,
    });
  },

  // Convert to map
  toMap(condition: UpdateUserAddressCondition): Record<string, any> {
    return {
      params: condition.params.map((param) => param /* param.toMap() */), // TODO: Replace when Param is converted
    };
  },

  // Convert to JSON
  toJson(condition: UpdateUserAddressCondition): string {
    return JSON.stringify(ConditionUtils.toMap(condition));
  },

  // Create from map
  fromMap(map: Record<string, any>): UpdateUserAddressCondition {
    try {
      return UpdateUserAddressConditionUtils.create({
        params:
          map["params"] && map["params"] !== ""
            ? map["params"].map((e: any) => e /* Param.fromMap(e) */) // TODO: Replace when Param is converted
            : [],
      });
    } catch (e) {
      console.error(e);
      return UpdateUserAddressConditionUtils.empty();
    }
  },

  // Create from JSON
  fromJson(source: string): UpdateUserAddressCondition {
    return UpdateUserAddressConditionUtils.fromMap(JSON.parse(source));
  },

  // Convert to string
  toString(condition: UpdateUserAddressCondition): string {
    return `UpdateUserAddressCondition(${condition.params.toString()})`;
  },

  // Validate
  validate(condition: UpdateUserAddressCondition): boolean {
    return condition.params.length > 0;
  },

  // TODO: Uncomment when Policy interface is ready
  // fromPolicy(policy: Policy): IUpdateUserAddressCondition {
  //   return (
  //     policy.conditions.find((c) => c.cls === ALLOW_UPDATE_USER_ADDRESS_CONDITION) ||
  //     UpdateUserAddressConditionUtils.empty()
  //   ) as IUpdateUserAddressCondition;
  // },

  // indexFromPolicy(policy: Policy): number {
  //   return policy.conditions.findIndex((c) => c.cls === ALLOW_UPDATE_USER_ADDRESS_CONDITION);
  // },
};
